import dayjs from 'dayjs';
import { useEffect, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  Button,
  DateInput,
  LoadingOverlay,
  ResponsiveModalBottomSheet,
  TimeSlotPicker,
  toast,
} from '@/components';
import type { Availability, Booking } from '@/models';
import { BookingService } from '@/services/bookingService';
import { useBrandStore } from '@/store';

interface ReorderBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  booking: Booking | null;
}

const extractTimeFromAvailabilityId = (availabilityId: string) =>
  dayjs(availabilityId).format('HH:mm');

const ReorderBottomSheet: FC<ReorderBottomSheetProps> = ({
  isOpen,
  onClose,
  booking,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getMaxAdvanceBookingDays } = useBrandStore();

  const [localDate, setLocalDate] = useState<Date | null>(new Date());
  const [localTime, setLocalTime] = useState('');
  const [selectedAvailabilityId, setSelectedAvailabilityId] = useState('');
  const [availabilities, setAvailabilities] = useState<Availability[]>([]);
  const [loading, setLoading] = useState(false);
  const [showTimeError, setShowTimeError] = useState(false);
  const [isReordering, setIsReordering] = useState(false);

  useEffect(() => {
    if (!isOpen || !booking) return;

    const date = new Date();
    const time = extractTimeFromAvailabilityId(booking.availabilityId);

    setLocalDate(date);
    setLocalTime(time);
    setSelectedAvailabilityId(booking.availabilityId);
    setShowTimeError(false);
  }, [booking, isOpen]);

  useEffect(() => {
    if (!isOpen || !booking || !localDate) {
      return;
    }

    const loadAvailabilities = async () => {
      setLoading(true);
      try {
        const dateString = localDate.toISOString().split('T')[0];
        const customerCount = booking.customers.length;

        const availabilityList = await BookingService.getAvailabilities(
          dateString,
          booking.storeId.toString(),
          customerCount,
        );

        const now = dayjs();
        const isToday = dayjs(localDate).isSame(now, 'day');

        const filteredAvailabilities = isToday
          ? availabilityList.filter((availability) =>
              dayjs(availability.availabilityId).isAfter(now),
            )
          : availabilityList;

        setAvailabilities(filteredAvailabilities);
      } catch (error) {
        console.error('Failed to load availabilities for reorder:', error);
        setAvailabilities([]);
      } finally {
        setLoading(false);
      }
    };

    void loadAvailabilities();
  }, [booking, isOpen, localDate]);

  useEffect(() => {
    if (!isOpen) return;

    if (availabilities.length === 0) {
      setLocalTime('');
      setSelectedAvailabilityId('');
      return;
    }

    const sameTimeAvailable = localTime
      ? availabilities.find(
          (availability) =>
            availability.available &&
            extractTimeFromAvailabilityId(availability.availabilityId) ===
              localTime,
        )
      : undefined;

    const firstAvailable = availabilities.find((availability) => {
      return availability.available;
    });

    const selectedAvailability = sameTimeAvailable ?? firstAvailable;

    if (!selectedAvailability) {
      setLocalTime('');
      setSelectedAvailabilityId('');
      return;
    }

    const selectedTime = extractTimeFromAvailabilityId(
      selectedAvailability.availabilityId,
    );

    setLocalTime(selectedTime);
    setSelectedAvailabilityId(selectedAvailability.availabilityId);
  }, [availabilities, isOpen, localTime]);

  const handleConfirm = async () => {
    if (!selectedAvailabilityId) {
      setShowTimeError(true);
      return;
    }

    if (!booking) return;

    setIsReordering(true);

    try {
      await BookingService.reorderBookingFromHistory(
        booking,
        selectedAvailabilityId,
      );

      onClose();
      void navigate('/booking/summary');
    } catch (error) {
      console.error('Failed to reorder booking:', error);

      toast.error(t('profile.home.errors.reorderUnavailable'));
      return;
    } finally {
      setIsReordering(false);
    }
  };

  return (
    <>
      <ResponsiveModalBottomSheet
        isOpen={isOpen}
        onClose={onClose}
        title={t('profile.home.actions.reorder')}
        closable={!isReordering}
        heightClass="min-h-[80vh] max-h-[85vh]"
        desktopHeightClass="md:min-h-[80vh] md:max-h-[85vh]"
        stickyFooter={
          <div className="px-4 py-4">
            <Button
              type="button"
              variant="primary"
              size="lg"
              disabled={isReordering}
              onClick={() => {
                void handleConfirm();
              }}
            >
              {t('common.confirm')}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4 px-4 pt-2 pb-6">
          <DateInput
            label={t('dateTime.selectDate')}
            value={localDate}
            onChange={(newDate) => {
              setLocalDate(newDate);
              setShowTimeError(false);
            }}
            minDate={new Date()}
            maxDate={dayjs().add(getMaxAdvanceBookingDays(), 'day').toDate()}
          />

          <div className="flex w-full flex-col gap-1">
            <div className="text-secondary-text text-lg leading-7 font-medium">
              {t('dateTime.selectTime')}
            </div>
            <TimeSlotPicker
              availabilities={availabilities}
              selectedAvailabilityId={selectedAvailabilityId}
              loading={loading}
              onSelect={(availability) => {
                const timeStr = extractTimeFromAvailabilityId(
                  availability.availabilityId,
                );
                setLocalTime(timeStr);
                setSelectedAvailabilityId(availability.availabilityId);
                setShowTimeError(false);
              }}
            />
          </div>

          {showTimeError ? (
            <div className="text-error text-base leading-5 font-normal">
              {t('validation.timeRequired')}
            </div>
          ) : null}
        </div>
      </ResponsiveModalBottomSheet>

      <LoadingOverlay isVisible={isReordering} />
    </>
  );
};

export default ReorderBottomSheet;
